import { API } from "aws-amplify";
import _ from "lodash";
import {
  GET_QUESTIONS_TO_REVIEW,
  GET_QUESTIONS_BY_USER,
  ACCEPT_QUESTION,
  REJECT_QUESTION,
  SUBMIT_NEW_QUESTION,
  GET_SUBJECT_AREAS,
  UPLOAD_FAQ_CMS_IMAGE,
  MARK_SUBMITTED_FAQ_FOR_DELETION,
  CLEAR_FAQ_CMS_STATE,
  DELETE_FAQ,
  REJECT_FAQ_DELETION_SUGGESTION,
  GET_FAQ_CMS_SETTINGS,
  SAVE_FAQ_CMS_SETTINGS
} from "./types";

import {getEnvironmentName} from "../../utils"
const environment = getEnvironmentName()
const config = require("../../app_configuration").CONFIGURATION[environment];
// todo duplicate code in other actions apiRequestFixedParams
const apiRequestFixedParams = {
  Version: config.RESPONSUM_API_VERSION,
  Api: config.RESPONSUM_API,
  AccountType: config.RESPONSUM_API_RESOURCE_TYPE.PRIVATE
};
const FAQ_CMS_API_ENDPOINT = "es";

export const getQuestionsToReview = (customFields = undefined) => async (dispatch, getState) => {
  const action = {
    type: GET_QUESTIONS_TO_REVIEW,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    action.payload = await getSubmittedFaqsApiCall(false, state, customFields);
  } catch (err) {
    action.payload.data = [];
  }

  dispatch(action);
};

const MAP_OF_KEYS_FOR_NEW_QUESTION = {
  question: "question",
  subjectArea: "subject_area",
  htmlResponse: "answer",
  voiceResponse: "answer_voice"
};
const MAP_OF_QUESTION_KEYS = {
  ...MAP_OF_KEYS_FOR_NEW_QUESTION,
  user: "user",
  dateSubmitted: "last_update",
  status: "status"
};
const CUSTOM_FIELDS_KEY = "customFields";

/**
 * Trashcan icon (i.e. mark for deletion action) is shown for approved questions which are not yet marked for
 * deletion or deleted.
 * @returns {boolean} - True if question in the table can be marked for deletion. False otherwise.
 */
const questionCanBeMarkedForDeletion = (docId, docData, allDocs) => {
  if (docData.status === "NEW") {
    // New (i.e. non-reviewed) question cannot be marked for deletion
    return false;
  }

  if (docData.status !== "APPROVED") {
    return false;
  }

  // Approved questions can be marked for deletion but we need to be sure that they were not already marked and deleted
  const originalDocId = docData.id; // Docs from the audit index have a reference to the original doc

  const docsReferencedByTheCurrentDoc = allDocs.filter(doc => {
    if (doc._id === docId) {
      return false;
    }

    if (doc._id === originalDocId) {
      return true;
    }

    const docReferencedId = _.get(doc, "_source.id", undefined);

    if (!docReferencedId) {
      return false;
    }

    return docReferencedId === originalDocId;
  });

  // If there already is a question referenced by the current question, it means the current approved question was
  // already marked for deletion (and possibly also deleted)
  return docsReferencedByTheCurrentDoc.length === 0;
};

/**
 * A user can directly delete only his/her own questions which are not yet reviewed (i.e. status is "NEW").
 * @returns {boolean} - True if question can be directly deleted, false otherwise.
 */
const questionCanBeDirectlyDeleted = (docData, currentUserUsername) => {
  if (!docData || !currentUserUsername) {
    return false;
  }

  return docData.status === "NEW" && docData.user === currentUserUsername;
};

const prepareQuestionsDataForComponent = (data, customFields, currentUserUsername) => {
  return data.map(item => {
    const src = _.get(item, "_source", {});
    const cleanItem = { docId: item._id };

    for (let key in MAP_OF_QUESTION_KEYS) {
      cleanItem[key] = _.get(src, MAP_OF_QUESTION_KEYS[key], "");
    }

    cleanItem.canBeMarkedForDeletion = questionCanBeMarkedForDeletion(item._id, src, data);
    cleanItem.canBeDirectlyDeleted = questionCanBeDirectlyDeleted(src, currentUserUsername);

    if (!customFields) {
      return cleanItem;
    }

    for (const customField of customFields) {
      if (src.hasOwnProperty(customField.sourceFieldName)) {
        cleanItem[customField.name] = src[customField.sourceFieldName];
      }
    }

    return cleanItem;
  });
};

const prepareQuestionDataForApiCall = questionData => {
  const questionItem = {};

  for (let key in MAP_OF_KEYS_FOR_NEW_QUESTION) {
    const newKey = MAP_OF_KEYS_FOR_NEW_QUESTION[key];
    questionItem[newKey] = _.get(questionData, key, "");
  }

  if (questionData.hasOwnProperty(CUSTOM_FIELDS_KEY)) {
    questionItem[CUSTOM_FIELDS_KEY] = questionData[CUSTOM_FIELDS_KEY];
  }

  return questionItem;
};

const getSubmittedFaqsApiCall = async (includeReviewedQuestions, state, customFields) => {
  const customFieldsKeys = customFields ? customFields.map(customField => customField.sourceFieldName) : [];

  const requestData = {
    body: {
      AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
      ApiCall: "GetSubmittedFaqs",
      IncludeReviewedQuestions: includeReviewedQuestions,
      ChatbotId: state.connections.publicChatbotProps.data.GUID,
      CustomFields: customFieldsKeys,
      ...apiRequestFixedParams
    }
  };
  const response = await API.post(config.AWS_API_NAME, FAQ_CMS_API_ENDPOINT, requestData);
  const { code, data } = response;
  const cleanData = prepareQuestionsDataForComponent(data, customFields, state.auth.cognitoUser.username);
  return {
    data: cleanData,
    status: code
  };
};

export const getQuestionsByUser = () => async (dispatch, getState) => {
  const action = {
    type: GET_QUESTIONS_BY_USER,
    payload: { status: 500 }
  };
  const state = getState();

  try {
    action.payload = await getSubmittedFaqsApiCall(true, state);
  } catch (err) {
    action.payload.data = [];
  }

  dispatch(action);
};

export const acceptQuestion = questionData => async (dispatch, getState) => {
  const action = {
    type: ACCEPT_QUESTION,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    const preparedQuestionData = prepareQuestionDataForApiCall(questionData);
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "UpdateFaqAndPublishIt",
        ChatbotId: state.connections.publicChatbotProps.data.GUID,
        DocId: questionData.docId,
        Doc: preparedQuestionData,
        ...apiRequestFixedParams
      }
    };
    const response = await API.post(config.AWS_API_NAME, FAQ_CMS_API_ENDPOINT, requestData);
    action.payload.status = response.code;
  } catch (err) {}

  dispatch(action);
};

export const rejectQuestion = questionData => async (dispatch, getState) => {
  const action = {
    type: REJECT_QUESTION,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "RejectFaq",
        ChatbotId: state.connections.publicChatbotProps.data.GUID,
        DocId: questionData.docId,
        ...apiRequestFixedParams
      }
    };
    const response = await API.post(config.AWS_API_NAME, FAQ_CMS_API_ENDPOINT, requestData);
    action.payload.status = response.code;
  } catch (err) {}

  dispatch(action);
};

export const submitNewQuestion = questionData => async (dispatch, getState) => {
  const action = {
    type: SUBMIT_NEW_QUESTION,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    const preparedQuestionData = prepareQuestionDataForApiCall(questionData);
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "InsertFaQ",
        ChatbotId: state.connections.publicChatbotProps.data.GUID,
        Doc: preparedQuestionData,
        ...apiRequestFixedParams
      }
    };
    const { docId } = questionData;
    if (docId) {
      requestData.body.DocId = docId;
    }
    const response = await API.post(config.AWS_API_NAME, FAQ_CMS_API_ENDPOINT, requestData);
    action.payload.status = response.code;
  } catch (err) {}

  dispatch(action);
};

export const getSubjectAreas = () => async (dispatch, getState) => {
  const action = {
    type: GET_SUBJECT_AREAS,
    payload: { status: 500, data: [] }
  };

  const state = getState();

  try {
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "GetSubjectAreas",
        ChatbotId: state.connections.publicChatbotProps.data.GUID,
        ...apiRequestFixedParams
      }
    };
    const response = await API.post(config.AWS_API_NAME, FAQ_CMS_API_ENDPOINT, requestData);
    const { code, data } = response;
    action.payload.status = code;
    action.payload.data = data.map(area => ({ value: area, label: area }));
  } catch (err) {}

  dispatch(action);
};

export const uploadFaqCmsImage = (blobInfo, success, failure, progress) => async (dispatch, getState) => {
  const action = {
    type: UPLOAD_FAQ_CMS_IMAGE,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "UploadFaqImage",
        Filename: blobInfo.filename(),
        Mime: blobInfo.blob().type,
        Base64: blobInfo.base64(),
        ...apiRequestFixedParams
      }
    };

    progress(1);
    const response = await API.post(config.AWS_API_NAME, "multimedia", requestData);
    action.payload.status = response.code;
    progress(99);
    success(response.uri);
  } catch (err) {
    failure("There was an error while uploading your image");
  }

  dispatch(action);
};

export const markSubmittedFaqForDeletion = questionData => async (dispatch, getState) => {
  const action = {
    type: MARK_SUBMITTED_FAQ_FOR_DELETION,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "MarkFaqForDeletion",
        ChatbotId: state.connections.publicChatbotProps.data.GUID,
        DocId: questionData.docId,
        ...apiRequestFixedParams
      }
    };
    const response = await API.post(config.AWS_API_NAME, FAQ_CMS_API_ENDPOINT, requestData);
    action.payload.status = response.code;
  } catch (err) {}

  dispatch(action);
};

export const deleteQuestion = (questionData, dontStoreAuditRecord) => async (dispatch, getState) => {
  const action = {
    type: DELETE_FAQ,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "DeleteFaq",
        ChatbotId: state.connections.publicChatbotProps.data.GUID,
        DocId: questionData.docId,
        DontStoreAuditRecord: dontStoreAuditRecord === true, // This needs to be a boolean for the backend
        ...apiRequestFixedParams
      }
    };
    const response = await API.post(config.AWS_API_NAME, FAQ_CMS_API_ENDPOINT, requestData);
    action.payload.status = response.code;
  } catch (err) {}

  dispatch(action);
};

export const rejectDeletionSuggestion = questionData => async (dispatch, getState) => {
  const action = {
    type: REJECT_FAQ_DELETION_SUGGESTION,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "RejectFaqDeletionSuggestion",
        ChatbotId: state.connections.publicChatbotProps.data.GUID,
        DocId: questionData.docId,
        ...apiRequestFixedParams
      }
    };
    const response = await API.post(config.AWS_API_NAME, FAQ_CMS_API_ENDPOINT, requestData);
    action.payload.status = response.code;
  } catch (err) {}

  dispatch(action);
};

export const clearFaqCmsState = () => {
  return { type: CLEAR_FAQ_CMS_STATE };
};

export const getFaqCmsSettings = () => async (dispatch, getState) => {
  const action = {
    type: GET_FAQ_CMS_SETTINGS,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "GetFaqCmsSettings",
        Tenant: state.auth.cognitoUser.attributes["custom:tenant"],
        ...apiRequestFixedParams
      }
    };
    const response = await API.post(config.AWS_API_NAME, "users/new", requestData);
    action.payload.status = response.code;
    action.payload.data = response.data;
  } catch (err) {}

  dispatch(action);
};

export const saveFaqCmsSettings = settings => async (dispatch, getState) => {
  const action = {
    type: SAVE_FAQ_CMS_SETTINGS,
    payload: { status: 500 }
  };

  const state = getState();

  try {
    const requestData = {
      body: {
        AccessToken: state.auth.cognitoUser.signInUserSession.accessToken.jwtToken,
        ApiCall: "SaveFaqCmsSettings",
        Tenant: state.auth.cognitoUser.attributes["custom:tenant"],
        Settings: settings,
        ...apiRequestFixedParams
      }
    };
    const response = await API.post(config.AWS_API_NAME, "users/new", requestData);
    action.payload.status = response.code;
  } catch (err) {}

  dispatch(action);
};
